import { ref, computed, nextTick, type Ref } from 'vue'

interface DropdownOption {
  value: string | number
  label: string
}

interface UseKeyboardDropdownNavigationOptions<T extends DropdownOption> {
  options: Ref<T[]>
  modelValue: Ref<string | number | undefined>
  isOpen: Ref<boolean>
  disabled?: Ref<boolean>
  buttonRef?: Ref<HTMLButtonElement | null>
  onSelect?: (option: T) => void
}

export function useKeyboardDropdownNavigation<T extends DropdownOption>({
  options,
  modelValue,
  isOpen,
  disabled,
  buttonRef,
  onSelect,
}: UseKeyboardDropdownNavigationOptions<T>) {
  const focusedIndex = ref(-1)

  const handleKeydown = (event: KeyboardEvent) => {
    if (disabled?.value) {
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (!isOpen.value) {
          isOpen.value = true
          const currentIndex = options.value.findIndex(option => option.value === modelValue.value)
          focusedIndex.value = currentIndex >= 0 ? currentIndex : 0
        }
        else {
          focusedIndex.value = Math.min(focusedIndex.value + 1, options.value.length - 1)
        }
        break

      case 'ArrowUp':
        event.preventDefault()
        if (!isOpen.value) {
          isOpen.value = true
          const currentIndex = options.value.findIndex(option => option.value === modelValue.value)
          focusedIndex.value = currentIndex >= 0 ? currentIndex : options.value.length - 1
        }
        else {
          focusedIndex.value = Math.max(focusedIndex.value - 1, 0)
        }
        break

      case 'Enter':
      case ' ':
        event.preventDefault()
        if (isOpen.value && focusedIndex.value >= 0) {
          const selectedOption = options.value[focusedIndex.value]
          if (selectedOption && onSelect) {
            onSelect(selectedOption)
          }
        }
        else if (!isOpen.value) {
          isOpen.value = true
          const currentIndex = options.value.findIndex(option => option.value === modelValue.value)
          focusedIndex.value = currentIndex >= 0 ? currentIndex : 0
        }
        break

      case 'Escape':
        event.preventDefault()
        if (isOpen.value) {
          closeDropdown()
        }
        break
    }
  }

  const closeDropdown = () => {
    isOpen.value = false
    focusedIndex.value = -1
    nextTick(() => {
      buttonRef?.value?.focus()
    })
  }

  const openDropdown = () => {
    if (!disabled?.value) {
      if (!isOpen.value) {
        // Find current selection index when opening
        const currentIndex = options.value.findIndex(option => option.value === modelValue.value)
        focusedIndex.value = currentIndex >= 0 ? currentIndex : 0
      }
      isOpen.value = !isOpen.value
    }
  }

  const selectOption = (option: T) => {
    if (onSelect) {
      onSelect(option)
    }
    closeDropdown()
  }

  const isOptionFocused = (index: number) => {
    return focusedIndex.value === index
  }

  return {
    focusedIndex,
    handleKeydown,
    closeDropdown,
    openDropdown,
    selectOption,
    isOptionFocused,
  }
}
